// src/components/NavBar.tsx
import * as React from "react";
import {
  AppBar,
  Box,
  Container,
  Divider,
  Drawer,
  IconButton,
  Link as MUILink,
  List,
  ListItemButton,
  ListItemText,
  Popper,
  Toolbar,
  Typography,
  useMediaQuery,
  Paper,
  ClickAwayListener,
  Collapse,
  Button,
  Stack,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import FacebookOutlinedIcon from "@mui/icons-material/FacebookOutlined";
import InstagramIcon from "@mui/icons-material/Instagram";
import YouTubeIcon from "@mui/icons-material/YouTube";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import PhoneInTalkOutlinedIcon from "@mui/icons-material/PhoneInTalkOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import SvgIcon from "@mui/material/SvgIcon";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import diamond from "../assests/diamond.jpg";
import gold from "../assests/gold.webp";
import polki from "../assests/diamond polki.webp";
import bands from "../assests/wedding band.webp";
import eng from "../assests/engagement.webp";
import { useQuery } from "@apollo/client";
import { ANNOUNCEMENT_BANNER_QUERY } from "../graphql/announcementBanner";
import logo from "../assests/logo.webp";
import { COLLECTIONS_QUERY } from "../graphql/collections";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";

type MegaColumnItem = {
  label: string;
  href?: string;
  icon?: React.ReactNode;
  targetType?: string;
  metal?: string;
};

type MegaColumn = {
  title: string;
  subTitle: string | "";
  items: MegaColumnItem[];
};

type MegaItem = {
  label: string;
  columns?: MegaColumn[];
  promo?: { imageUrl?: string; alt?: string; ctaLabel?: string; href?: string };
};

const TopBar = styled("div")(() => ({
  width: "100%",
  background: "#000",
  color: "#fff",
  fontSize: 13,
  letterSpacing: 0.2,
  textAlign: "center",
  padding: "8px 0",
}));

const BrandLogo = styled("img")(({ theme }) => ({
  width: "auto",
  height: 100,
  flex: "0 0 auto",
  position: "relative",
  top: "10px",
  [theme.breakpoints.down("md")]: { height: 60, top: "5px" },
  [theme.breakpoints.down("sm")]: { height: 60, left: "-31px" },
}));

const BrandText = styled("h1")(({ theme }) => ({
  margin: 0,
  fontWeight: 400,
  lineHeight: 1,
  fontFamily: '"Times New Roman", Times, serif',
  color: "#111",
  textTransform: "uppercase",
  whiteSpace: "nowrap",
  fontSize: 62,
  letterSpacing: 12,
  [theme.breakpoints.down("lg")]: { fontSize: 54, letterSpacing: 10 },
  [theme.breakpoints.down("md")]: { fontSize: 33, letterSpacing: 6 },
  [theme.breakpoints.down("sm")]: {
    fontSize: 28,
    letterSpacing: 0,
    position: "relative",
    left: "-51px",
  },
}));

const Brand = styled("div")(() => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 2,
  maxWidth: "100%",
  minWidth: 0,
}));

const SearchRoot = styled("div")(() => ({
  position: "relative",
  display: "flex",
  alignItems: "center",
  gap: 10,
}));

const PrimaryNav = styled("nav")(() => ({
  width: "100%",
  display: "flex",
  justifyContent: "center",
  gap: 40,
  padding: "14px 0 10px",
  borderTop: "1px solid #eee",
  borderBottom: "1px solid #eee",
}));

const TabButton = styled(Button)(() => ({
  fontSize: 12,
  letterSpacing: 1.6,
  color: "#111",
  padding: 0,
  minWidth: 0,
  borderRadius: 0,
  position: "relative",
  "&:after": {
    content: '""',
    position: "absolute",
    left: 0,
    bottom: -10,
    height: 2,
    width: 0,
    background: "#111",
    transition: "width .22s ease",
  },
  "&[data-active='true']:after, &:hover:after, &:focus-visible:after": {
    width: "100%",
  },
}));

const MegaPaper = styled(Paper)(() => ({
  borderRadius: 0,
  boxShadow: "0 30px 60px rgba(0, 0, 0, 0.12)",
  width: "100%",
  padding: "28px 40px 28px",
  border: "1px solid #eee",
  marginInline: "auto",
}));

const Columns = styled("div")(({ theme }) => ({
  display: "grid",
  gap: 0,
  alignItems: "start",
  "& > div": { padding: "0 28px" },
  "& > div + div": { borderLeft: "1px solid #eee" },
  [theme.breakpoints.down("lg")]: {},
}));

const ColTitle = styled(Typography)({
  fontWeight: 700,
  fontSize: 16,
  letterSpacing: 0.8,
  marginBottom: 14,
});

const ColSubTitle = styled(Typography)({
  fontWeight: 700,
  fontSize: 14,
  letterSpacing: 0.8,
  marginBottom: 14,
});

const ItemLink = styled(MUILink)({
  display: "flex",
  alignItems: "center",
  gap: 10,
  color: "#111",
  textDecoration: "none",
  padding: "10px 0",
  fontSize: 15,
  "&:hover": { textDecoration: "underline" },
});

const PromoWrap = styled("div")(({ theme }) => ({
  display: "none",
  [theme.breakpoints.up("lg")]: {
    display: "block",
    position: "relative",
    overflow: "hidden",
    borderRadius: 4,
    borderLeft: "1px solid #eee",
    paddingLeft: 28,
    paddingRight: 28,
  },
}));

const TumblrIcon = (props: any) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path d="M14 3h-3a4 4 0 0 0-4 4v4H5v3h2v3.5C7 20.985 9.015 23 11.5 23H14v-3h-1.5C11.673 20 11 19.327 11 18.5V14h3v-3h-3V7a1 1 0 0 1 1-1H14V3z" />
  </SvgIcon>
);

const SOCIAL_LINKS = {
  facebook: "https://web.facebook.com/haroonsharifjewellers/?_rdc=1&_rdr#",
  instagram: "https://www.instagram.com/haroonsharifjewellers/?hl=en",
  tumblr: "https://goldjewelleryinpak.tumblr.com/",
  youtube: "https://www.youtube.com/c/haroonsharifjewellers",
};

const JewelIcon = (props: any) => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#111"
    strokeWidth="1.5"
    {...props}
  >
    <path d="M12 3l3.5 4.5L12 21 8.5 7.5 12 3z" />
    <path d="M12 3H6l2.5 4.5H12m0-4.5h6l-2.5 4.5H12" />
  </svg>
);

const CATEGORY_PATH = "/products-listing";

const buildCategoryHref = (targetType?: string, metal?: string) => {
  if (!targetType && !metal) return "";
  const params = new URLSearchParams();
  if (targetType) params.set("type", targetType);
  if (metal) params.set("metal", metal);
  return `${CATEGORY_PATH}?${params.toString()}`;
};

const NAV: MegaItem[] = [
  {
    label: "GOLD",
    columns: [
      {
        title: "SHOP GOLD",
        subTitle: "",
        items: [
          {
            label: "Rings",
            icon: <JewelIcon />,
            targetType: "ring",
            metal: "gold",
          },
          {
            label: "Earrings",
            icon: <JewelIcon />,
            targetType: "earrings",
            metal: "gold",
          },
          {
            label: "Pendants",
            icon: <JewelIcon />,
            targetType: "necklace",
            metal: "gold",
          },
          {
            label: "Bracelets",
            icon: <JewelIcon />,
            targetType: "bracelets",
            metal: "gold",
          },
          {
            label: "Karas",
            icon: <JewelIcon />,
            targetType: "karas",
            metal: "gold",
          },
          {
            label: "Bridal Sets",
            icon: <JewelIcon />,
            targetType: "bridal-sets",
            metal: "gold",
          },
        ],
      },
    ],
    promo: { imageUrl: gold, alt: "Gold jewellery collection", href: "#" },
  },
  {
    label: "DIAMOND",
    columns: [
      {
        title: "SHOP DIAMOND",
        subTitle: "",
        items: [
          {
            label: "Rings",
            icon: <JewelIcon />,
            targetType: "ring",
            metal: "diamond",
          },
          {
            label: "Earrings",
            icon: <JewelIcon />,
            targetType: "earrings",
            metal: "diamond",
          },
          {
            label: "Pendants",
            icon: <JewelIcon />,
            targetType: "necklace",
            metal: "diamond",
          },
          {
            label: "Bracelets",
            icon: <JewelIcon />,
            targetType: "bracelets",
            metal: "diamond",
          },
          {
            label: "Karas",
            icon: <JewelIcon />,
            targetType: "karas",
            metal: "diamond",
          },
          {
            label: "Bridal Sets",
            icon: <JewelIcon />,
            targetType: "bridal-sets",
            metal: "diamond",
          },
        ],
      },
    ],
    promo: {
      imageUrl: diamond,
      alt: "Diamond jewellery collection",
      href: "#",
    },
  },
  {
    label: "DIAMOND POLKI",
    columns: [
      {
        title: "SHOP POLKI",
        subTitle: "",
        items: [
          {
            label: "Rings",
            icon: <JewelIcon />,
            targetType: "ring",
            metal: "diamond-polki",
          },
          {
            label: "Earrings",
            icon: <JewelIcon />,
            targetType: "earrings",
            metal: "diamond-polki",
          },
          {
            label: "Bracelets",
            icon: <JewelIcon />,
            targetType: "bracelets",
            metal: "diamond-polki",
          },
          {
            label: "Karas",
            icon: <JewelIcon />,
            targetType: "karas",
            metal: "diamond-polki",
          },
          {
            label: "Bridal Sets",
            icon: <JewelIcon />,
            targetType: "bridal-sets",
            metal: "diamond-polki",
          },
        ],
      },
    ],
    promo: { imageUrl: polki, alt: "Polki bridal jewellery", href: "#" },
  },
  {
    label: "WEDDING BANDS",
    columns: [
      {
        title: "SHOP BY METAL",
        subTitle: "FOR HIM",
        items: [
          {
            label: "Platinum",
            icon: <JewelIcon />,
            targetType: "wedding band",
            metal: "platinum",
          },
          {
            label: "Palladium",
            icon: <JewelIcon />,
            targetType: "wedding band",
            metal: "palladium",
          },
          {
            label: "Gold",
            icon: <JewelIcon />,
            targetType: "wedding band",
            metal: "gold",
          },
        ],
      },
    ],
    promo: { imageUrl: bands, alt: "Wedding bands promo", href: "#" },
  },
  {
    label: "ENGAGEMENT RINGS",
    columns: [
      {
        title: "ENGAGEMENT RINGS",
        subTitle: "FOR HER",
        items: [
          {
            label: "Gold",
            icon: <JewelIcon />,
            targetType: "ring",
            metal: "gold",
          },
          {
            label: "Diamond",
            icon: <JewelIcon />,
            targetType: "ring",
            metal: "diamond",
          },
        ],
      },
    ],
    promo: { imageUrl: eng, alt: "Engagement rings promo", href: "#" },
  },
];

export default function NavBar() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [drawer, setDrawer] = React.useState(false);

  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [openIndex, setOpenIndex] = React.useState<number | null>(null);
  const open = Boolean(anchorEl) && openIndex !== null;

  const { data } = useQuery(ANNOUNCEMENT_BANNER_QUERY);
  const { data: collectionsData } = useQuery(COLLECTIONS_QUERY, {
    fetchPolicy: "cache-and-network",
  });

  const banner = data?.announcementBanner;
  const bannerMessage = (banner?.message ?? "").trim();
  const showBanner = bannerMessage.length > 0;

  const COLLECTION_NAV: MegaItem = React.useMemo(() => {
    const items =
      (collectionsData?.collections || []).map((c: any) => ({
        label: c.name,
        href: `/collections/${c.slug}`,
      })) || [];

    return {
      label: "COLLECTIONS",
      columns: [
        {
          title: "OUR COLLECTIONS",
          subTitle: "",
          items: items.length
            ? items
            : [{ label: "No collections yet", href: "/collections" }],
        },
      ],
    };
  }, [collectionsData]);

  const BLOGS_NAV: MegaItem = {
    label: "BLOGS",
    columns: [
      {
        title: "FROM OUR JOURNAL",
        subTitle: "Stories, guides & updates from Haroon Sharif",
        items: [
          {
            label: "Read All Articles",
            href: "/blogs",
            icon: <ArticleOutlinedIcon sx={{ fontSize: 18 }} />,
          },
        ],
      },
    ],
  };

  const FULL_NAV: MegaItem[] = React.useMemo(
    () => [...NAV, COLLECTION_NAV, BLOGS_NAV],
    [COLLECTION_NAV],
  );

  const handleEnter = (index: number) => (e: React.MouseEvent<HTMLElement>) => {
    if (isMobile) return;
    setAnchorEl(e.currentTarget);
    setOpenIndex(index);
  };

  const handleLeave = () => {
    setAnchorEl(null);
    setOpenIndex(null);
  };

  const handleFocus = (index: number) => (e: React.FocusEvent<HTMLElement>) => {
    if (isMobile) return;
    setAnchorEl(e.currentTarget);
    setOpenIndex(index);
  };

  const onKeyDown =
    (index: number) => (e: React.KeyboardEvent<HTMLElement>) => {
      if (isMobile) return;
      if (e.key === "Enter" || e.key === "ArrowDown" || e.key === " ") {
        setAnchorEl(e.currentTarget as HTMLElement);
        setOpenIndex(index);
      } else if (e.key === "Escape") {
        handleLeave();
      }
    };

  const handleNavigateContact = () => {
    navigate("/contact");
    setDrawer(false);
  };

  const handleNavigateCategory = (targetType?: string, metal?: string) => {
    const url = buildCategoryHref(targetType, metal);
    if (!url) return;
    navigate(url);
    setDrawer(false);
  };

  return (
    <>
      {showBanner && <TopBar>{bannerMessage}</TopBar>}

      <AppBar
        position="sticky"
        color="inherit"
        elevation={0}
        sx={{ borderBottom: "1px solid #eee", bgcolor: "#fff" }}
      >
        <Container maxWidth="lg">
          <Toolbar
            sx={{
              minHeight: 110,
              height: { lg: 120 },
              display: "grid",
              gridTemplateColumns: { xs: "auto 1fr auto", md: "auto 1fr auto" },
              alignItems: "center",
              px: { xs: 1.5, sm: 2 },
              "& > *": { minWidth: 0 },
            }}
          >
            <Box
              sx={{
                display: { xs: "none", md: "flex" },
                alignItems: "center",
                gap: 2,
              }}
            >
              <SearchRoot>
                <Typography
                  sx={{
                    textTransform: "uppercase",
                    letterSpacing: 1,
                    fontSize: 14,
                    color: "#333",
                  }}
                >
                  {/* Search… */}
                </Typography>
              </SearchRoot>
            </Box>

            <Box
              sx={{ display: "flex", justifyContent: "center", minWidth: 0 }}
            >
              <Brand
                onClick={() => navigate("/")}
                style={{ cursor: "pointer" }}
                aria-label="Haroon Sharif"
              >
                <BrandLogo src={logo} alt="Haroon Sharif monogram" />
                <BrandText>HAROON SHARIF</BrandText>
              </Brand>
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: 1,
              }}
            >
              {!isMobile ? (
                <>
                  <IconButton aria-label="Account">{/* icon */}</IconButton>
                  <IconButton aria-label="Bag">{/* icon */}</IconButton>
                </>
              ) : (
                <IconButton
                  aria-label="Open menu"
                  onClick={() => setDrawer(true)}
                  sx={{ right: isMobile ? "-14px" : 0 }}
                >
                  <MenuIcon />
                </IconButton>
              )}
            </Box>
          </Toolbar>
        </Container>

        {!isMobile && (
          <PrimaryNav role="menubar" aria-label="Primary">
            {FULL_NAV.map((item, i) => (
              <TabButton
                key={item.label}
                role="menuitem"
                aria-haspopup={!!item.columns}
                aria-expanded={open && openIndex === i ? "true" : "false"}
                data-active={open && openIndex === i ? "true" : "false"}
                onMouseEnter={handleEnter(i)}
                onFocus={handleFocus(i)}
                onKeyDown={onKeyDown(i)}
              >
                {item.label}
              </TabButton>
            ))}
          </PrimaryNav>
        )}
      </AppBar>

      {/* DESKTOP POPPER (uses FULL_NAV, not NAV) */}
      {!isMobile && (
        <Popper
          open={open}
          anchorEl={anchorEl}
          placement="bottom-start"
          modifiers={[
            {
              name: "offset",
              options: { offset: ({ reference }: any) => [-reference.x, 8] },
            },
            { name: "preventOverflow", options: { altAxis: false } },
          ]}
          style={{ zIndex: 1300 }}
        >
          <Box sx={{ width: "100vw" }} onMouseLeave={handleLeave}>
            <ClickAwayListener onClickAway={handleLeave}>
              <MegaPaper>
                {openIndex !== null &&
                  (() => {
                    const current = FULL_NAV[openIndex];
                    if (!current) return null;

                    const cols = current.columns || [];
                    const colCount = cols.length;
                    const hasPromo = !!current.promo;

                    let gridTemplateColumns: string;

                    if (colCount <= 1) {
                      gridTemplateColumns = hasPromo
                        ? "minmax(0, 1.2fr) minmax(0, 1fr)"
                        : "minmax(0, 1fr)";
                    } else if (colCount === 2) {
                      gridTemplateColumns = hasPromo
                        ? "repeat(2, minmax(0,1fr)) minmax(0,1fr)"
                        : "repeat(2, minmax(0,1fr))";
                    } else {
                      gridTemplateColumns = hasPromo
                        ? "repeat(3, minmax(0,1fr)) minmax(0,1fr)"
                        : "repeat(4, minmax(0,1fr))";
                    }

                    return (
                      <Columns style={{ gridTemplateColumns }}>
                        {cols.map((col) => (
                          <div key={col.title}>
                            <ColTitle
                              variant="subtitle1"
                              sx={{ fontSize: "13px" }}
                            >
                              {col.title}
                            </ColTitle>
                            <ColSubTitle
                              variant="subtitle2"
                              sx={{ fontSize: "12px" }}
                            >
                              {col.subTitle}
                            </ColSubTitle>

                            <Box>
                              {col.items.map((it) => {
                                const isCategory = !!(
                                  it.targetType || it.metal
                                );
                                const url = isCategory
                                  ? buildCategoryHref(it.targetType, it.metal)
                                  : it.href || "";

                                return (
                                  <ItemLink
                                    key={it.label}
                                    href={url || "#"}
                                    underline="none"
                                    onClick={(e) => {
                                      if (!url) return;
                                      e.preventDefault();
                                      navigate(url);
                                      handleLeave();
                                    }}
                                  >
                                    {it.icon}
                                    <span style={{ fontSize: "13px" }}>
                                      {it.label}
                                    </span>
                                    <KeyboardArrowRightRoundedIcon
                                      sx={{ marginLeft: "auto", fontSize: 18 }}
                                    />
                                  </ItemLink>
                                );
                              })}
                            </Box>
                          </div>
                        ))}

                        {hasPromo && (
                          <PromoWrap>
                            <Box
                              component="img"
                              loading="lazy"
                              decoding="async"
                              src={current.promo?.imageUrl}
                              alt={current.promo?.alt || "Promo"}
                              sx={{
                                width: "100%",
                                height: 320,
                                objectFit: "cover",
                                display: "block",
                                borderRadius: 4,
                              }}
                            />
                          </PromoWrap>
                        )}
                      </Columns>
                    );
                  })()}
              </MegaPaper>
            </ClickAwayListener>
          </Box>
        </Popper>
      )}

      {/* MOBILE DRAWER uses FULL_NAV too */}
      <Drawer
        open={drawer}
        onClose={() => setDrawer(false)}
        PaperProps={{ sx: { width: "92vw", maxWidth: 420 } }}
      >
        <Box sx={{ p: 2 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: 26,
              mb: 1,
              fontFamily: '"Times New Roman", Times, serif',
            }}
          >
            Haroon Sharif
          </Typography>
        </Box>
        <Divider />
        <Box
          sx={{
            bgcolor: "#000",
            color: "#fff",
            textAlign: "center",
            fontSize: 13,
            letterSpacing: 0.2,
            py: 1,
            px: 2,
          }}
        >
          Free Shipping Lahore wide
        </Box>
        <Divider />

        <MobileMenu
          items={FULL_NAV}
          onNavigateContact={handleNavigateContact}
          onNavigateCategory={handleNavigateCategory}
          onClose={() => setDrawer(false)}
          navigateTo={(href) => {
            navigate(href);
            setDrawer(false);
          }}
        />
      </Drawer>
    </>
  );
}

type MobileMenuProps = {
  items: MegaItem[];
  onNavigateContact: () => void;
  onNavigateCategory?: (targetType?: string, metal?: string) => void;
  onClose: () => void;
  navigateTo: (href: string) => void;
};

function MobileMenu({
  items,
  onNavigateContact,
  onNavigateCategory,
  navigateTo,
}: MobileMenuProps) {
  const [openIdx, setOpenIdx] = React.useState<number | null>(null);
  const year = new Date().getFullYear();

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <List disablePadding sx={{ flexGrow: 1 }}>
        {items.map((item, i) => {
          const expanded = openIdx === i;

          return (
            <Box key={item.label}>
              <ListItemButton onClick={() => setOpenIdx(expanded ? null : i)}>
                <ListItemText
                  primaryTypographyProps={{
                    sx: { fontSize: 16, letterSpacing: 1, fontWeight: 600 },
                  }}
                  primary={item.label}
                />
                <KeyboardArrowDownRoundedIcon
                  sx={{
                    transform: expanded ? "rotate(180deg)" : "none",
                    transition: "transform .2s",
                  }}
                />
              </ListItemButton>

              <Collapse in={expanded} timeout="auto" unmountOnExit>
                <Box sx={{ pl: 2, pr: 1, pb: 1 }}>
                  {(item.columns || []).map((col) => (
                    <Box key={col.title} sx={{ py: 1 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1 }}>
                        {col.title}
                      </Typography>
                      <Typography sx={{ fontWeight: 700, fontSize: 12, mb: 1 }}>
                        {col.subTitle}
                      </Typography>

                      {col.items.map((it) => {
                        const isCategory = !!(it.targetType || it.metal);

                        return (
                          <ListItemButton
                            key={it.label}
                            sx={{ pl: 1 }}
                            onClick={() => {
                              if (isCategory) {
                                onNavigateCategory?.(it.targetType, it.metal);
                              } else if (it.href) {
                                navigateTo(it.href);
                              }
                            }}
                          >
                            <ListItemText primary={it.label} />
                          </ListItemButton>
                        );
                      })}
                    </Box>
                  ))}
                </Box>
              </Collapse>

              <Divider />
            </Box>
          );
        })}
      </List>

      <Box sx={{ px: 3, pt: 2, pb: 3, borderTop: "1px solid #eee" }}>
        <Typography
          sx={{
            fontFamily: `"Bodoni Moda", serif`,
            fontWeight: 700,
            fontSize: 18,
            mb: 1,
          }}
        >
          Contact Us
        </Typography>
        <Typography
          sx={{ fontSize: 13, color: "text.secondary", mb: 2, maxWidth: 260 }}
        >
          Get in touch with our team for personalised assistance.
        </Typography>

        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{ mb: 3, maxWidth: 260 }}
        >
          {[
            { label: "Chat", icon: <ChatBubbleOutlineIcon /> },
            { label: "Call", icon: <PhoneInTalkOutlinedIcon /> },
            { label: "Email", icon: <MailOutlineIcon /> },
          ].map((item) => (
            <Box
              key={item.label}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                cursor: "pointer",
              }}
              onClick={onNavigateContact}
            >
              <IconButton
                sx={{
                  border: "1px solid rgba(0,0,0,0.12)",
                  mb: 0.5,
                  width: 40,
                  height: 40,
                }}
              >
                {item.icon}
              </IconButton>
              <Typography sx={{ fontSize: 12 }}>{item.label}</Typography>
            </Box>
          ))}
        </Stack>

        <Typography
          sx={{
            fontFamily: `"Bodoni Moda", serif`,
            fontWeight: 700,
            fontSize: 16,
            mb: 1,
          }}
        >
          Let&apos;s be friends
        </Typography>

        <Stack direction="row" spacing={1.5} sx={{ mb: 3 }}>
          <IconButton
            component="a"
            href={SOCIAL_LINKS.facebook}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
          >
            <FacebookOutlinedIcon />
          </IconButton>
          <IconButton
            component="a"
            href={SOCIAL_LINKS.instagram}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
          >
            <InstagramIcon />
          </IconButton>
          <IconButton
            component="a"
            href={SOCIAL_LINKS.tumblr}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
          >
            <TumblrIcon />
          </IconButton>
          <IconButton
            component="a"
            href={SOCIAL_LINKS.youtube}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
          >
            <YouTubeIcon />
          </IconButton>
        </Stack>
      </Box>

      <Box
        sx={{
          bgcolor: "#000",
          color: "#fff",
          textAlign: "center",
          fontSize: 13,
          letterSpacing: 0.2,
          py: 1,
          px: 2,
        }}
      >
        © {year} Haroon Sharif Jewellers — All rights reserved
      </Box>
    </Box>
  );
}
